import { useCallback } from 'react';

import {
  FetchMaterialsFilters,
  MaterialClientID,
  ChangeMaterialsFiltersFunc,
  FetchMaterialsFilterMaterials,
  MaterialCategoryID,
  FetchMaterialsFilterFields
} from '../../../../materials/materialsTypes';
import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../categories/categoriesTypes';
import { TeamUUID } from '../../../../teams/teamsTypes';

import { ThreeDStockShowFiltersLayout } from '../../layouts/ThreeDStockShowFiltersLayout';

import { ThreeDStockMaterialsFiltersAppliedFilters } from './components/ThreeDStockMaterialsFiltersAppliedFilters';
import { ThreeDStockMaterialsFiltersCategoriesFilter } from './components/ThreeDStockMaterialsFiltersCategoriesFilter';
import { ThreeDStockMaterialsFiltersFavoritesFilter } from './components/ThreeDStockMaterialsFiltersFavoritesFilter';

import { MaterialsBrandsFilter } from '../../../../materials/components/filters/MaterialsBrandsFilter';
import { MaterialsBlockedFilter } from '../../../../materials/components/filters/MaterialsBlockedFilter';

import { Translate } from '../../../../../helpers/Translate';
import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';
import { CheckPermissions } from '../../../../../helpers/CheckPermissions';

import { IconsEnum } from '../../../../../assets/icons/types';

import { MaterialsPermissions } from '../../../../materials/materialsConstants';

import { words } from '../../../../../locales/keys';

interface ThreeDStockMaterialsFiltersProps {
  teamUuid?: TeamUUID;
  clientIds?: MaterialClientID[];
  defaultMaterialCategoryIds?: MaterialCategoryID[];
  defaultMaterialParentCategoryIds?: MaterialCategoryID[];
  materialsFilters: FetchMaterialsFilters;
  changeMaterialsFilters: ChangeMaterialsFiltersFunc;
  filterMaterials: FetchMaterialsFilterMaterials;
  closeFilters?: () => void;
  clearFilters?: (persistFiltersFields?: FetchMaterialsFilterFields[]) => void;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    categories: { id: CategoryID; localizedName: CategoryLocalizedName }[]
  ) => void;
  changeCategory: (
    changeFilters?: FetchMaterialsFilters,
    removeFilters?: string[]
  ) => void;
  isModal?: boolean;
}

function ThreeDStockMaterialsFilters({
  teamUuid,
  clientIds,
  clearFilters,
  defaultMaterialCategoryIds,
  defaultMaterialParentCategoryIds,
  materialsFilters,
  changeMaterialsFilters,
  filterMaterials,
  closeFilters,
  checkedCategories,
  setCheckedCategories,
  changeCategory,
  isModal
}: ThreeDStockMaterialsFiltersProps) {
  const handleClearFilters = useCallback<() => void>(() => {
    clearFilters();
    setCheckedCategories([]);
  }, [clearFilters, setCheckedCategories]);

  const handleChangeCategories = useCallback<ChangeMaterialsFiltersFunc>(
    (changedFilters, removeFilters) => {
      clearFilters([FetchMaterialsFilterFields.NAME]);
      changeMaterialsFilters(changedFilters, removeFilters);
    },
    [changeMaterialsFilters, clearFilters]
  );

  return (
    <ThreeDStockShowFiltersLayout onClose={closeFilters} isModal={isModal}>
      <div className="flex justify-between pl-4 pr-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-t-xl py-1.5">
        <div className="text-sm uppercase font-semibold leading-8">
          <Translate id={words.refineSearch} />
        </div>

        <PureIconButtonHelper
          className="py-1.5 pl-1.5 pr-1.5 rounded-full inline-flex items-center leading-5 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 text-gray-500 dark:text-gray-400 transition-colors duration-150 ease-out"
          onClick={closeFilters}
          icon={IconsEnum.X}
          iconClassName="h-5 w-5"
          dataGa="close-filters"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        <div id="filters-materials" className="flex flex-col space-y-6">
          <ThreeDStockMaterialsFiltersAppliedFilters
            changeMaterialsFilters={changeMaterialsFilters}
            materialsFilters={materialsFilters}
            filterMaterials={filterMaterials}
            checkedCategories={checkedCategories}
            setCheckedCategories={setCheckedCategories}
            clearFilters={handleClearFilters}
            defaultMaterialCategoryIds={defaultMaterialCategoryIds}
            defaultMaterialParentCategoryIds={defaultMaterialParentCategoryIds}
          />

          <CheckPermissions
            action={MaterialsPermissions.READ_MATERIALS_CATEGORIES_FILTER}
          >
            <ThreeDStockMaterialsFiltersCategoriesFilter
              changeMaterialsFilters={handleChangeCategories}
              defaultMaterialCategoryIds={defaultMaterialCategoryIds}
              checkedCategories={checkedCategories}
              setCheckedCategories={setCheckedCategories}
              changeCategory={changeCategory}
              materialsFilters={materialsFilters}
            />
          </CheckPermissions>

          <CheckPermissions
            action={
              MaterialsPermissions.READ_MATERIALS_FAVORITE_CHECKBOX_FILTER
            }
          >
            <ThreeDStockMaterialsFiltersFavoritesFilter
              teamUuid={teamUuid}
              materialsFilters={materialsFilters}
              changeMaterialsFilters={changeMaterialsFilters}
            />
          </CheckPermissions>

          <MaterialsBrandsFilter
            clientIds={clientIds}
            materialsFilters={materialsFilters}
            changeMaterialsFilters={changeMaterialsFilters}
          />

          <CheckPermissions
            action={MaterialsPermissions.READ_MATERIALS_BLOCKED_FILTER}
          >
            <MaterialsBlockedFilter
              materialsFilters={materialsFilters}
              changeMaterialsFilters={changeMaterialsFilters}
            />
          </CheckPermissions>
        </div>
      </div>
    </ThreeDStockShowFiltersLayout>
  );
}

export default ThreeDStockMaterialsFilters;
